import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useGitSyncStore } from '../../store/gitSyncStore';
import useScopeGit from '../../hooks/useScopeGit';
import GitConnectModal, { connectableAccounts } from '../GitConnectModal';
import GitConflictModal from '../GitConflictModal';
import { ConfirmModal } from '../ui/Modal';
import { useNotificationStore } from '../../store/notificationStore';
import { accountsForTeamRepo, joinOptions, teamRepoLabel } from '../../services/git/teamRepo';
import GitJoinTeamModal from './GitJoinTeamModal';
import { TEAM_NOT_LOCAL } from '../../services/git/scopeLocalGitSync';
import { useScopeProgress } from '../../services/git/scopeProgress';

/**
 * Projects, boards & documents on the Git page (git sync Stage 2, S2.3): one
 * repository for your own items and one per team (owner decisions
 * 02.10.2026). Since the page redesign (05.10.2026) the scopes are pills
 * beside the workspaces and the selected one fills the detail card, so their
 * state, actions and dialogs live here once and are read through
 * `useScopeGitHost()` — by the pills (GitTargetPills) and the head
 * (GitScopeHead). Outside the desktop app there is no host: no scopes.
 */
const ScopeGitContext = createContext(null);

/** The scopes and their actions, or null outside a host (web). */
export const useScopeGitHost = () => useContext(ScopeGitContext);

export default function GitScopeHost({ children }) {
  const { t } = useTranslation();
  const { scopes, busyKey, connect, sync, pull, refreshStatus, conflict, resolveConflicts, cancelConflict, removeRepo, saveVersion, addRemote, stopTracking } =
    useScopeGit();
  const byWorkspace = useGitSyncStore((s) => s.byWorkspace);
  const [connecting, setConnecting] = useState(null);
  // T10: the personal scope tracked on this device gets a remote through the same dialog, in "Add a remote" mode.
  const [addingRemote, setAddingRemote] = useState(null);
  // Card 84628c27: joining the team's stored repository, and the owner/admin's move / remove (each behind a warning).
  const [joining, setJoining] = useState(null);
  const [confirmMove, setConfirmMove] = useState(null);
  const [moving, setMoving] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(null);
  const connectProgress = useScopeProgress(connecting?.key);
  const remoteProgress = useScopeProgress(addingRemote?.key);

  /** Join in one click when exactly one signed-in account reaches the repository; otherwise ask how to sign in. */
  const startJoin = async (scope) => {
    const usable = accountsForTeamRepo(await connectableAccounts().catch(() => []), scope.teamRepo);
    if (usable.length !== 1) {
      setJoining(scope);
      return;
    }
    try {
      await connect(scope, joinOptions(scope.teamRepo, usable[0]));
    } catch (err) {
      useNotificationStore.getState().error(err?.message || String(err), t('gitTeamRepo.notJoined', { team: scope.name }));
    }
  };

  const linkedKey = scopes.filter((s) => s.git).map((s) => s.key).join(',');
  useEffect(() => {
    const check = () => scopes.forEach((s) => refreshStatus(s));
    check();
    window.addEventListener('git-refresh-status', check);
    return () => window.removeEventListener('git-refresh-status', check);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedKey]);

  const value = useMemo(
    () => ({
      scopes,
      busyKey,
      byWorkspace,
      sync,
      pull,
      saveVersion,
      stopTracking,
      startJoin,
      openConnect: setConnecting,
      openAddRemote: setAddingRemote,
      askMove: setConfirmMove,
      askRemove: setConfirmRemove,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the actions are the hook's own, stable per render of it
    [scopes, busyKey, byWorkspace, sync, pull, saveVersion, stopTracking]
  );

  return (
    <ScopeGitContext.Provider value={value}>
      {children}
      <GitConnectModal
        isOpen={Boolean(connecting)}
        onClose={() => {
          setConnecting(null);
          setMoving(false);
        }}
        workspace={connecting ? { id: connecting.key, name: connecting.kind === 'team' ? `${connecting.name} boards and documents` : 'apiboo boards and documents' } : null}
        subtitle={
          moving && connecting
            ? t('gitTeamRepo.moveSubtitle', { team: connecting.name })
            : connecting?.kind === 'team'
              ? `Keep ${connecting.name}’s projects, boards and documents in a repository`
              : 'Keep your projects, boards and documents in your own repository'
        }
        onConnect={async (opts) => {
          if (await connect(connecting, moving ? { ...opts, move: true } : opts)) {
            setConnecting(null);
            setMoving(false);
          }
        }}
        loading={Boolean(connecting && busyKey === connecting.key)}
        progress={connectProgress}
        allowLocal={connecting?.kind === 'personal' && !moving}
        localUnavailable={connecting?.kind === 'team' ? TEAM_NOT_LOCAL : ''}
      />
      <GitConnectModal
        isOpen={Boolean(addingRemote)}
        onClose={() => setAddingRemote(null)}
        mode="addRemote"
        workspace={addingRemote ? { id: addingRemote.key, name: 'apiboo boards and documents', git: addingRemote.git } : null}
        subtitle="Push the versions of your projects, boards and documents saved on this device to a repository"
        onConnect={async (opts) => {
          if (await addRemote(addingRemote, opts)) setAddingRemote(null);
        }}
        loading={Boolean(addingRemote && busyKey === addingRemote.key)}
        progress={remoteProgress}
      />
      <GitJoinTeamModal
        isOpen={Boolean(joining)}
        onClose={() => setJoining(null)}
        scope={joining}
        onJoin={async (opts) => {
          if (await connect(joining, opts)) setJoining(null);
        }}
        loading={Boolean(joining && busyKey === joining.key)}
      />
      <ConfirmModal
        isOpen={Boolean(confirmMove)}
        onClose={() => setConfirmMove(null)}
        onConfirm={() => {
          setMoving(true);
          setConnecting(confirmMove);
        }}
        title={t('gitTeamRepo.moveTitle')}
        message={confirmMove ? t('gitTeamRepo.moveWarning', { team: confirmMove.name, repo: teamRepoLabel(confirmMove.teamRepo) }) : ''}
        confirmText={t('gitTeamRepo.moveConfirm')}
        cancelText={t('common.cancel')}
        type="warning"
      />
      <ConfirmModal
        isOpen={Boolean(confirmRemove)}
        onClose={() => setConfirmRemove(null)}
        onConfirm={() => removeRepo(confirmRemove)}
        title={t('gitTeamRepo.removeTitle')}
        message={confirmRemove ? t('gitTeamRepo.removeWarning', { repo: teamRepoLabel(confirmRemove.teamRepo) }) : ''}
        confirmText={t('gitTeamRepo.removeConfirm')}
        cancelText={t('common.cancel')}
        type="danger"
      />
      <GitConflictModal
        isOpen={Boolean(conflict)}
        onClose={cancelConflict}
        conflicts={conflict?.conflicts || []}
        onResolve={resolveConflicts}
        loading={Boolean(conflict && busyKey === conflict.scope.key)}
      />
    </ScopeGitContext.Provider>
  );
}
